import { makeAutoObservable, runInAction } from 'mobx';
import { fx } from '@/platform/feedback';
import { api } from '@/api';
import { invoke } from '@/api/signalr';
import type { RootStore } from './RootStore';
import type { Letters } from '@/domain/scoring';
import { score as computeScore } from '@/domain/scoring';

export type SubmittedWord = {
  word: string;
  l1: number; l2: number; l3: number;
  rawScore: number;
  submittedAt: string;
};

export type RejectedWord = {
  word: string;
  reason: string;
  rejectedAt: string;
};

export type OpponentEntry = {
  playerId: string;
  nickname: string;
  wordCount?: number;
  totalScore?: number;
  isConnected: boolean;
};

export class RoundStore {
  roundId: string | null = null;
  roundIndex = 0;
  totalRounds = 1;
  letters: Letters | null = null;
  startedAtUtc: string | null = null;
  endsAtUtc: string | null = null;
  serverClockOffsetMs = 0;
  justStarted = false;

  myWords: SubmittedWord[] = [];
  myRejections: RejectedWord[] = [];
  isSubmitting = false;

  opponents: OpponentEntry[] = [];
  progressVisibility: 'WordCount' | 'Score' | 'Hidden' = 'WordCount';

  private clockSyncTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly root: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  startClockSync() {
    this.stopClockSync();
    this.clockSyncTimer = setInterval(async () => {
      try {
        const r = await invoke<{ serverNowUtc: string }>('Ping');
        runInAction(() => {
          this.serverClockOffsetMs = new Date(r.serverNowUtc).getTime() - Date.now();
        });
      } catch { /* ignore */ }
    }, 30_000);
  }

  stopClockSync() {
    if (this.clockSyncTimer !== null) {
      clearInterval(this.clockSyncTimer);
      this.clockSyncTimer = null;
    }
  }

  get isActive(): boolean {
    if (!this.roundId || !this.endsAtUtc) return false;
    return Date.now() + this.serverClockOffsetMs < new Date(this.endsAtUtc).getTime();
  }

  get remainingMs(): number {
    if (!this.endsAtUtc) return 0;
    return Math.max(0, new Date(this.endsAtUtc).getTime() - (Date.now() + this.serverClockOffsetMs));
  }

  get myTotalScore(): number {
    return this.myWords.reduce((sum, w) => sum + w.rawScore, 0);
  }

  clearJustStarted(): void {
    this.justStarted = false;
  }

  reset() {
    this.roundId = null;
    this.roundIndex = 0;
    this.totalRounds = 1;
    this.letters = null;
    this.startedAtUtc = null;
    this.endsAtUtc = null;
    this.justStarted = false;
    this.myWords = [];
    this.myRejections = [];
    this.opponents = [];
  }

  async loadFromServer(code: string): Promise<void> {
    const round = await api.round.get(code);
    runInAction(() => this.applyRoundDto(round));
  }

  // Backfills opponent progress via REST after a (re)subscribe — the SignalR snapshot
  // itself carries no opponent data (Q03: on reload mid-round, opponents stayed empty
  // until the next OpponentProgress push, which may not arrive for the rest of the round).
  async loadProgress(code: string): Promise<void> {
    const progress = await api.round.getProgress(code);
    runInAction(() => this.applyProgress(progress));
  }

  applyServerRound(data: unknown): void {
    const r = data as {
      roundId: string; roundIndex: number; totalRounds: number;
      letters: string; startedAtUtc: string; endsAtUtc: string;
      serverNowUtc: string;
      myWords?: Array<{ word: string; rawScore: number; submittedAt: string }>;
      myRejections?: Array<{ word: string; reason: string; rejectedAt: string }>;
    };
    const letters = r.letters.split('') as [string, string, string];
    this.roundId = r.roundId;
    this.roundIndex = r.roundIndex;
    this.totalRounds = r.totalRounds;
    this.letters = letters;
    this.startedAtUtc = r.startedAtUtc;
    this.endsAtUtc = r.endsAtUtc;
    this.serverClockOffsetMs = new Date(r.serverNowUtc).getTime() - Date.now();
    // Новый раунд — прогресс соперников с прошлого раунда больше не актуален (N6):
    // без сброса виджет первые секунды показывал последний известный OpponentProgress,
    // который из-за той же путаницы AccountId/Player.Id чаще всего оказывался самим игроком.
    this.opponents = [];
    this.myWords = (r.myWords ?? []).map((w) => {
      const positions = computeScore(w.word, letters)?.positions ?? { l1: 0, l2: 0, l3: 0 };
      return { word: w.word, ...positions, rawScore: w.rawScore, submittedAt: w.submittedAt };
    });
    this.myRejections = (r.myRejections ?? []).map((rj) => ({
      word: rj.word, reason: rj.reason, rejectedAt: rj.rejectedAt,
    }));
  }

  applyProgress(data: unknown): void {
    const p = data as {
      visibility?: string;
      players?: Array<{ playerId: string | number; nickname: string; wordCount?: number; totalScore?: number; isConnected: boolean }>;
    };
    if (!p.players) return;
    const myId = this.root.auth.playerId;
    this.progressVisibility = (p.visibility as 'WordCount' | 'Score' | 'Hidden') ?? 'WordCount';
    this.opponents = p.players
      .filter((pl) => String(pl.playerId) !== myId)
      .map((pl) => ({
        playerId: String(pl.playerId),
        nickname: pl.nickname,
        wordCount: pl.wordCount,
        totalScore: pl.totalScore,
        isConnected: pl.isConnected,
      }));
  }

  private applyRoundDto(round: import('@/api/dto').RoundDto): void {
    this.roundId = round.roundId;
    this.roundIndex = round.roundIndex;
    this.totalRounds = round.totalRounds;
    const letters = round.letters.split('') as [string, string, string];
    this.letters = letters;
    this.startedAtUtc = round.startedAtUtc;
    this.endsAtUtc = round.endsAtUtc;
    this.serverClockOffsetMs = new Date(round.serverNowUtc).getTime() - Date.now();

    this.myWords = round.myWords.map(w => {
      const positions = computeScore(w.word, letters)?.positions ?? { l1: 0, l2: 0, l3: 0 };
      return {
        word: w.word,
        l1: positions.l1,
        l2: positions.l2,
        l3: positions.l3,
        rawScore: w.rawScore,
        submittedAt: w.submittedAt,
      };
    });

    this.myRejections = round.myRejections.map(r => ({
      word: r.word,
      reason: r.reason,
      rejectedAt: r.rejectedAt,
    }));
  }

  async submitWord(code: string, word: string): Promise<void> {
    if (this.isSubmitting || !this.letters) return;
    this.isSubmitting = true;
    const trimmed = word.trim();
    try {
      const res = await api.round.submitWord(code, trimmed);
      runInAction(() => {
        if (res.accepted) {
          const positions = computeScore(res.word, this.letters!)?.positions ?? { l1: 0, l2: 0, l3: 0 };
          this.myWords.unshift({
            word: res.word,
            l1: positions.l1,
            l2: positions.l2,
            l3: positions.l3,
            rawScore: res.rawScore,
            submittedAt: new Date().toISOString(),
          });
          this.root.ui.flash('accepted', res.rawScore);
          fx.accept(res.isUnique);
        } else {
          this.myRejections.unshift({
            word: res.word,
            reason: res.reason,
            rejectedAt: new Date().toISOString(),
          });
          this.root.ui.flash('rejected', 0, res.reason);
          fx.reject();
        }
        this.isSubmitting = false;
      });
    } catch (e) {
      runInAction(() => { this.isSubmitting = false; });
      throw e;
    }
  }
}
